import React from "react";
import { Helmet } from "react-helmet-async";
import {
  FaHeartbeat,
  FaClinicMedical,
  FaPills,
  FaUtensils,
  FaRunning,
  FaSmokingBan,
  FaHeart,
  FaHome,
  FaCar,
  FaExclamationTriangle,
} from "react-icons/fa";
import { FiHome } from "react-icons/fi";
import {
  MdEmojiPeople,
  MdMedicalServices,
  MdOutlinePregnantWoman,
} from "react-icons/md";
import { Link } from "react-router-dom";

const PregnancySafety = () => {
  const safetyData = {
    prenatal: {
      title: "Prenatal Care",
      icon: <FaClinicMedical className="text-[#9ea3f5] text-2xl" />,
      tips: [
        {
          title: "Regular Checkups",
          content:
            "Monthly visits until 28 weeks, biweekly until 36 weeks, then weekly until delivery. Track your appointments with our calendar feature.",
          details: [
            "First visit: Confirm pregnancy and estimate due date",
            "16-20 weeks: Check fetal heartbeat and growth",
            "28 weeks: Glucose screening test",
            "36 weeks: Group B strep test",
          ],
        },
        {
          title: "Ultrasounds & Tests",
          content:
            "Essential monitoring for fetal development and early issue detection.",
          details: [
            "Dating scan: 8-14 weeks",
            "Nuchal translucency: 11-14 weeks",
            "Anatomy scan: 18-22 weeks",
            "Growth scan: 28-32 weeks (if needed)",
          ],
        },
        {
          title: "Prenatal Vitamins",
          content:
            "Critical nutrients for fetal development and maternal health.",
          details: [
            "Folic acid: 400-800mcg daily",
            "Iron: 27mg daily",
            "Calcium: 1000mg daily",
            "DHA: 200mg daily",
          ],
        },
      ],
    },
    medications: {
      title: "Safe Medications",
      icon: <FaPills className="text-[#e8bffb] text-2xl" />,
      tips: [
        {
          title: "Avoid Self-Medicating",
          content:
            "Many common drugs can harm fetal development. Always consult your OB-GYN.",
          details: [
            "NSAIDs (ibuprofen, aspirin): Avoid especially in third trimester",
            "Certain antibiotics: Like tetracyclines",
            "Retinoids: Can cause birth defects",
            "Herbal supplements: Many lack safety data",
          ],
        },
        {
          title: "Approved Medications",
          content:
            "Some medications are generally considered safe during pregnancy.",
          details: [
            "Acetaminophen: For pain and fever",
            "Certain antacids: For heartburn relief",
            "Prenatal vitamins: Specifically for pregnancy",
            "Some allergy meds: Like loratadine (check doctor)",
          ],
        },
      ],
    },
    lifestyle: {
      title: "Lifestyle Guidelines",
      icon: <MdEmojiPeople className="text-[#d099e2] text-2xl" />,
      tips: [
        {
          title: "Avoid Harmful Substances",
          content:
            "Stay away from tobacco, alcohol, and illicit drugs for your baby's health.",
          details: [
            "No safe amount of alcohol",
            "Tobacco increases risks",
            "Drugs may cause birth defects",
          ],
        },
        {
          title: "Stress Management",
          content: "Manage emotional well-being with support and self-care.",
          details: [
            "Practice yoga or meditation",
            "Seek support groups or therapy",
            "Get enough rest and sleep",
          ],
        },
      ],
    },
    nutrition: {
      title: "Nutrition & Hydration",
      icon: <FaUtensils className="text-[#ccdcfd] text-2xl" />,
      tips: [
        {
          title: "Healthy Eating",
          content:
            "Eat a balanced diet rich in whole grains, fruits, vegetables, and protein.",
          details: [
            "Avoid unpasteurized and raw items",
            "Limit caffeine to <200mg/day",
            "Eat small frequent meals",
          ],
        },
        {
          title: "Stay Hydrated",
          content: "Hydration helps blood volume and amniotic fluid.",
          details: [
            "Drink at least 8–10 glasses of water",
            "Avoid sugary drinks",
            "Coconut water is a good option",
          ],
        },
      ],
    },
    activity: {
      title: "Physical Activity",
      icon: <FaRunning className="text-[#f2a5e8] text-2xl" />,
      tips: [
        {
          title: "Exercise Safely",
          content: "Moderate activity benefits both mother and baby.",
          details: [
            "Walking, swimming, prenatal yoga are safe",
            "Avoid contact sports",
            "Listen to your body",
          ],
        },
        {
          title: "Benefits of Staying Active",
          content: "Improves mood, sleep, circulation, and labor readiness.",
          details: [
            "Reduces pain and fatigue",
            "Prevents excessive weight gain",
            "Strengthens muscles for labor",
          ],
        },
      ],
    },
    home: {
      title: "Home & Travel Safety",
      icon: <FaHome className="text-[#bbbefe] text-2xl" />,
      tips: [
        {
          title: "Home Hazards",
          content: "Keep your environment toxin-free and safe.",
          details: [
            "Avoid chemical fumes",
            "Ensure ventilation",
            "Use anti-slip mats",
          ],
        },
        {
          title: "Safe Travel Tips",
          content: "Be cautious while commuting or flying.",
          details: [
            "Wear seatbelt below belly",
            "Take breaks to stretch",
            "Consult doctor before flights",
          ],
        },
      ],
    },
    warning: {
      title: "Warning Signs",
      icon: <FaExclamationTriangle className="text-[#f87171] text-2xl" />,
      tips: [
        {
          title: "Urgent Symptoms",
          content: "Call your provider if you experience any of the following.",
          details: [
            "Severe one-sided abdominal pain",
            "Heavy bleeding",
            "Sudden swelling (face/hands)",
            "Reduced fetal movement after 28 weeks",
          ],
        },
        {
          title: "When to Call Your Provider",
          content: "Don't ignore these signs.",
          details: [
            "Persistent vomiting",
            "Severe headaches with vision issues",
            "Fever over 100.4°F",
            "Painful urination",
          ],
        },
      ],
    },
  };

  return (
    <div className="w-full font-nunito min-h-screen py-8 px-4 sm:px-6 lg:px-8">
   <Helmet>
    {/* Google Analytics */}
    <script
      async
      src="https://www.googletagmanager.com/gtag/js?id=G-YR8YMW9ERY"
    ></script>
    <script>
      {`
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', 'G-YR8YMW9ERY', {
          page_path: window.location.pathname,
        });
      `}
    </script>
    <title>Pregnancy Safety Tips - FlowHearts | Healthy Pregnancy Guide</title>
    <meta
      name="description"
      content="Discover FlowHearts' Pregnancy Safety Tips for prenatal care, safe medications, lifestyle advice, and warning signs to ensure a healthy pregnancy."
    />

    {/* Canonical URL */}
    <link rel="canonical" href="https://flowhearts.com/pregnancy-safety" />

    {/* Open Graph / Facebook */}
    <meta property="og:type" content="website" />
    <meta property="og:url" content="https://flowhearts.com/pregnancy-safety" />
    <meta
      property="og:title"
      content="Pregnancy Safety Tips - FlowHearts | Healthy Pregnancy Guide"
    />
    <meta
      property="og:description"
      content="FlowHearts' Pregnancy Safety Tips provide expert guidance on prenatal care, safe medications, lifestyle choices, and critical warning signs for a healthy pregnancy."
    />
    <meta
      property="og:image"
      content="https://flowhearts.com/images/pregnancy-safety-preview.jpg"
    />

    {/* Twitter */}
    <meta property="twitter:card" content="summary_large_image" />
    <meta property="twitter:url" content="https://flowhearts.com/pregnancy-safety" />
    <meta
      property="twitter:title"
      content="Pregnancy Safety Tips - FlowHearts | Healthy Pregnancy Guide"
    />
    <meta
      property="twitter:description"
      content="Explore FlowHearts' Pregnancy Safety Tips for prenatal care, safe medications, lifestyle advice, and key warning signs to support a healthy pregnancy."
    />
    <meta
      property="twitter:image"
      content="https://flowhearts.com/images/pregnancy-safety-preview.jpg"
    />

    {/* Structured Data */}
    <script type="application/ld+json">
      {`
        {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "FlowHearts Pregnancy Safety Tips",
          "url": "https://flowhearts.com/pregnancy-safety",
          "description": "FlowHearts' Pregnancy Safety Tips offer essential guidance on prenatal care, safe medications, lifestyle, and warning signs for a healthy pregnancy.",
          "applicationCategory": "HealthApplication",
          "operatingSystem": "Web",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "creator": {
            "@type": "Organization",
            "name": "FlowHearts"
          }
        }
      `}
    </script>
  </Helmet>
      <div className="max-w-8xl mx-auto">
        <div className="flex justify-start mb-6">
          <Link
            to="/"
            className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-md hover:bg-pink-50 transition-colors duration-200"
          >
            <FiHome className="text-pink-500" />
            <span className="text-purple-700 font-medium">Back to Home</span>
          </Link>
        </div>
        <div className="relative mb-10">
          <header className="text-center">
            <div className="flex justify-center mb-3">
              <MdOutlinePregnantWoman className="text-5xl text-[#9ea3f5]" />
            </div>
            <h2 className="text-4xl font-cormorant md:text-4xl font-bold text-[#5a4d8a] mb-2">
              Pregnancy Safety Guide
            </h2>
            <p className="text-lg text-[#6e5ba8]">
              Your complete resource for a healthy pregnancy journey
            </p>
          </header>
        </div>
        <div className="bg-white rounded-2xl shadow-lg p-6">
          {Object.entries(safetyData).map(([key, section]) => (
            <div key={key} className="mb-8">
              <div className="flex items-center mb-4">
                <div className="mr-4">{section.icon}</div>
                <h2 className="text-2xl font-bold text-[#5a4d8a]">
                  {section.title}
                </h2>
              </div>
              <div className="space-y-4">
                {section.tips.map((tip, index) => (
                  <div
                    key={index}
                    className="border-l-4 border-[#bbbefe] pl-4 py-3 bg-[#f9f7ff] rounded-r-lg"
                  >
                    <div className="flex items-center space-x-2">
                      <FaHeart className="text-[#d099e2] text-sm" />
                      <h3 className="font-semibold text-[#6e5ba8]">
                        {tip.title}
                      </h3>
                    </div>
                    <p className="text-[#5a4d8a] mt-2 pl-7">{tip.content}</p>
                    {tip.details && (
                      <ul className="list-disc pl-10 mt-3 text-[#5a4d8a] space-y-2">
                        {tip.details.map((detail, i) => (
                          <li key={i}>{detail}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div className="mt-10 text-center text-sm text-[#6e5ba8]">
            <p>
              Remember: Every pregnancy is unique. Consult your healthcare provider
              for personalized advice.
            </p>
          </div>
        </div>
        {/* Educational Section */}
        <div className="mt-12 bg-white rounded-3xl shadow-xl p-8">
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-[#5a4d8a] mb-6 flex items-center">
              <FaClinicMedical className="mr-3 text-[#9ea3f5]" />
              Starting Your Pregnancy Safely
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-[#f9f7ff] rounded-xl p-6 border border-[#bbbefe]">
                <div className="w-12 h-12 bg-[#9ea3f5] text-white rounded-full flex items-center justify-center text-xl mb-4 mx-auto">
                  1
                </div>
                <h3 className="text-lg font-semibold text-center text-[#6e5ba8] mb-2">
                  Find a Provider
                </h3>
                <p className="text-[#5a4d8a] text-center">
                  Choose a trusted OB-GYN or midwife early
                </p>
              </div>
              <div className="bg-[#f9f7ff] rounded-xl p-6 border border-[#bbbefe]">
                <div className="w-12 h-12 bg-[#9ea3f5] text-white rounded-full flex items-center justify-center text-xl mb-4 mx-auto">
                  2
                </div>
                <h3 className="text-lg font-semibold text-center text-[#6e5ba8] mb-2">
                  Create a Plan
                </h3>
                <p className="text-[#5a4d8a] text-center">
                  Schedule checkups and track milestones
                </p>
              </div>
              <div className="bg-[#f9f7ff] rounded-xl p-6 border border-[#bbbefe]">
                <div className="w-12 h-12 bg-[#9ea3f5] text-white rounded-full flex items-center justify-center text-xl mb-4 mx-auto">
                  3
                </div>
                <h3 className="text-lg font-semibold text-center text-[#6e5ba8] mb-2">
                  Build Support
                </h3>
                <p className="text-[#5a4d8a] text-center">
                  Join prenatal classes or support groups
                </p>
              </div>
            </div>
          </div>
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-[#5a4d8a] mb-6 flex items-center">
              <MdMedicalServices className="mr-3 text-[#d099e2]" />
              Science of Pregnancy Safety
            </h2>
            <div className="bg-gradient-to-br from-[#f9f7ff] to-[#e8bffb] rounded-xl p-6 border border-[#bbbefe] mb-6">
              <h3 className="text-xl font-semibold text-[#5a4d8a] mb-4">
                Key Safety Factors
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="bg-white p-4 rounded-lg">
                  <p className="font-semibold text-[#6e5ba8] mb-2">
                    Fetal Protection
                  </p>
                  <p className="text-[#5a4d8a]">
                    Avoiding harmful substances supports healthy development
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <p className="font-semibold text-[#6e5ba8] mb-2">
                    Maternal Health
                  </p>
                  <p className="text-[#5a4d8a]">
                    Regular monitoring reduces risks by up to 30%
                  </p>
                </div>
              </div>
              <p className="text-[#5a4d8a]">
                Studies emphasize prenatal care and lifestyle choices significantly improve pregnancy outcomes.
              </p>
            </div>
          </div>
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-[#5a4d8a] mb-6 flex items-center">
              <FaHeartbeat className="mr-3 text-[#f2a5e8]" />
              Benefits of Safe Practices
            </h2>
            <div className="bg-gradient-to-br from-[#ccdcfd] to-[#f9f7ff] rounded-xl p-6 border border-[#bbbefe]">
              <p className="text-[#5a4d8a] mb-4">
                Adopting safe pregnancy practices enhances maternal and fetal well-being.
              </p>
              <ul className="space-y-3 text-[#5a4d8a]">
                <li className="flex items-start">
                  <FaHeart className="text-[#d099e2] mr-2 mt-0.5" />
                  <span>Lower risk of complications</span>
                </li>
                <li className="flex items-start">
                  <FaHeart className="text-[#d099e2] mr-2 mt-0.5" />
                  <span>Improved energy and mood</span>
                </li>
                <li className="flex items-start">
                  <FaHeart className="text-[#d099e2] mr-2 mt-0.5" />
                  <span>Stronger fetal development</span>
                </li>
                <li className="flex items-start">
                  <FaHeart className="text-[#d099e2] mr-2 mt-0.5" />
                  <span>Easier labor preparation</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-[#5a4d8a] mb-6 flex items-center">
              <FaExclamationTriangle className="mr-3 text-[#f87171]" />
              Debunking Pregnancy Myths
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-[#fef2f2] rounded-xl p-6 border border-[#f87171]">
                <h3 className="text-lg font-semibold text-[#f87171] mb-3">
                  Myth: Exercise Is Risky
                </h3>
                <p className="text-[#5a4d8a]">
                  Reality: Moderate exercise is safe and beneficial
                </p>
              </div>
              <div className="bg-[#fef2f2] rounded-xl p-6 border border-[#f87171]">
                <h3 className="text-lg font-semibold text-[#f87171] mb-3">
                  Myth: All Meds Are Unsafe
                </h3>
                <p className="text-[#5a4d8a]">
                  Reality: Some medications are safe with guidance
                </p>
              </div>
              <div className="bg-[#fef2f2] rounded-xl p-6 border border-[#f87171]">
                <h3 className="text-lg font-semibold text-[#f87171] mb-3">
                  Myth: Stress Doesn’t Matter
                </h3>
                <p className="text-[#5a4d8a]">
                  Reality: Chronic stress can impact pregnancy
                </p>
              </div>
              <div className="bg-[#fef2f2] rounded-xl p-6 border border-[#f87171]">
                <h3 className="text-lg font-semibold text-[#f87171] mb-3">
                  Myth: Travel Is Always Safe
                </h3>
                <p className="text-[#5a4d8a]">
                  Reality: Consult doctor before long trips
                </p>
              </div>
            </div>
          </div>
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-[#5a4d8a] mb-6 flex items-center">
              <FaRunning className="mr-3 text-[#f2a5e8]" />
              Daily Safety Practices
            </h2>
            <div className="bg-gradient-to-br from-[#f9f7ff] to-[#ccdcfd] rounded-xl p-6 border border-[#bbbefe]">
              <p className="text-[#5a4d8a] mb-4">
                Simple habits to ensure a safe and healthy pregnancy.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h3 className="font-semibold text-[#6e5ba8] mb-2">Morning Check</h3>
                  <p className="text-[#5a4d8a] text-sm">
                    Monitor symptoms and take prenatal vitamins
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h3 className="font-semibold text-[#6e5ba8] mb-2">Safe Eating</h3>
                  <p className="text-[#5a4d8a] text-sm">
                    Choose pasteurized and nutrient-rich foods
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h3 className="font-semibold text-[#6e5ba8] mb-2">Gentle Activity</h3>
                  <p className="text-[#5a4d8a] text-sm">
                    Incorporate light exercise daily
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h3 className="font-semibold text-[#6e5ba8] mb-2">Evening Rest</h3>
                  <p className="text-[#5a4d8a] text-sm">
                    Practice relaxation techniques before bed
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-[#5a4d8a] mb-6 flex items-center">
              <MdMedicalServices className="mr-3 text-[#e8bffb]" />
              When to Seek Help
            </h2>
            <div className="bg-[#fef2f2] rounded-xl p-6 border border-[#f87171]">
              <p className="text-[#5a4d8a] mb-4">
                Contact your healthcare provider if you notice these signs.
              </p>
              <ul className="space-y-3 text-[#5a4d8a]">
                <li className="flex items-start">
                  <FaExclamationTriangle className="text-[#f87171] mr-2 mt-0.5" />
                  <span>Unusual pain or cramping</span>
                </li>
                <li className="flex items-start">
                  <FaExclamationTriangle className="text-[#f87171] mr-2 mt-0.5" />
                  <span>Changes in fetal movement</span>
                </li>
                <li className="flex items-start">
                  <FaExclamationTriangle className="text-[#f87171] mr-2 mt-0.5" />
                  <span>Severe nausea or dizziness</span>
                </li>
                <li className="flex items-start">
                  <FaExclamationTriangle className="text-[#f87171] mr-2 mt-0.5" />
                  <span>Signs of preterm labor</span>
                </li>
              </ul>
              <p className="text-[#5a4d8a] mt-4">
                Timely action can prevent complications and ensure safety.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PregnancySafety;